import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import type { LoanOfficer } from '@ld.consumer-portal/models';
import type { BranchOffice } from '@ld.consumer-portal/models';
import { TranslocoDirective } from '@jsverse/transloco';
import { errorIcon } from '@ld.consumer-portal/data-uri';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { BaseModalComponent } from '../base-modal/base-modal/base-modal.component';
import { LoggingService } from '@ld.consumer-portal/utils';

@Component({
  selector: 'ld-loan-officer-search-modal',
  templateUrl: './loan-officer-search-modal.component.html',
  styleUrl: './loan-officer-search-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    BaseModalComponent,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslocoDirective,
  ],
})
export class LoanOfficerSearchModalComponent {
  private readonly loggingService = inject(LoggingService);

  protected readonly errorIcon = errorIcon;

  readonly searchByNameFn = input.required<(name: string) => Promise<LoanOfficer[]>>();

  readonly searchByZipCodeFn = input.required<(zipCode: string) => Promise<BranchOffice[]>>();

  readonly getLoanOfficersByBranchFn = input.required<(branchId: string) => Promise<LoanOfficer[]>>();

  readonly selectOfficer = output<LoanOfficer>();

  readonly close = output<void>();

  protected readonly nameSearchControl = new FormControl('');

  protected readonly zipSearchControl = new FormControl('');

  protected readonly isLoading = signal(false);

  protected readonly searchResults = signal<LoanOfficer[]>([]);

  protected readonly errorMessage = signal<string | null>(null);

  protected readonly hasSearched = signal(false);

  protected readonly brokenImageUrls = signal(new Set<string>());

  protected readonly noResults = computed(
    () => this.hasSearched() && this.searchResults().length === 0 && !this.isLoading()
  );

  private readonly nameSearchValue = toSignal(
    this.nameSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      filter((value) => !!value && value.length >= 2)
    ),
    { initialValue: '' }
  );

  constructor() {
    effect(() => {
      const name = this.nameSearchValue();
      if (name && name.length >= 2) {
        this.searchByName(name);
      }
    });
  }

  protected onSearchByName(): void {
    const name = this.nameSearchControl.value?.trim();
    if (name && name.length >= 2) {
      this.searchByName(name);
    }
  }

  protected onSearchByZip(): void {
    const zip = this.zipSearchControl.value?.trim();
    if (zip && zip.length === 5) {
      this.searchByZipCode(zip);
    }
  }

  private async searchByName(name: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const results = await this.searchByNameFn()(name);
      this.searchResults.set(results || []);
    } catch (error) {
      this.loggingService.logException(error, 'Search error:');
      this.errorMessage.set('Unable to search. Please try again.');
      this.searchResults.set([]);
    } finally {
      this.hasSearched.set(true);
      this.isLoading.set(false);
    }
  }

  private async searchByZipCode(zipCode: string): Promise<void> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const branches = await this.searchByZipCodeFn()(zipCode);
      if (branches && branches.length > 0) {
        try {
          const officers = await this.getLoanOfficersByBranchFn()(branches[0].branchOfficeId);
          this.searchResults.set(officers || []);
        } catch {
          this.searchResults.set([]);
        }
      } else {
        this.searchResults.set([]);
      }
    } catch (error) {
      this.loggingService.logException(error, 'ZIP search error:');
      this.errorMessage.set('Unable to search by ZIP code. Please try again.');
      this.searchResults.set([]);
    } finally {
      this.hasSearched.set(true);
      this.isLoading.set(false);
    }
  }

  protected onSelectOfficer(officer: LoanOfficer): void {
    this.selectOfficer.emit(officer);
  }

  protected onClose(): void {
    this.close.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close.emit();
    }
  }

  protected onImageLoadError(url: string): void {
    this.brokenImageUrls.update((set) => new Set(set).add(url));
  }

  protected isBrokenImage(url: string | undefined): boolean {
    return url ? this.brokenImageUrls().has(url) : false;
  }

  protected getInitials(name: string | undefined): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  protected getDisplayName(officer: LoanOfficer): string {
    return officer.preferredName || 'Unknown';
  }

  protected getLocation(officer: LoanOfficer): string {
    const parts = [officer.city, officer.state].filter(Boolean);
    return parts.join(', ') || 'Location not available';
  }
}
